import React, { useCallback, useEffect, useState } from 'react';
import { Lock, Sparkles, AlertTriangle, X, Info } from 'lucide-react';
import {
  installPlanInterceptor, openPlans, fetchEntitlements,
  PLAN_BLOCKED_EVENT, PLAN_CHANGED_EVENT,
} from './planGuard';
import '../style/PlanGate.css';

const TITLES = {
  PLAN_REQUIRED: 'Choose a plan to continue',
  PLAN_READ_ONLY: 'Renew to make changes',
  PLAN_FEATURE_UNAVAILABLE: 'Upgrade to unlock this',
  PLAN_LIMIT_REACHED: 'Plan limit reached',
  PLAN_QUOTA_EXHAUSTED: 'Monthly limit reached',
  NOT_YOUR_RESTAURANT: 'Not your restaurant',
};

/* Shown automatically whenever any dashboard request is blocked by the plan */
export const PlanBlockedDialog = () => {
  const [block, setBlock] = useState(null);

  useEffect(() => {
    installPlanInterceptor();
    const onBlocked = (e) => setBlock(e.detail);
    window.addEventListener(PLAN_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(PLAN_BLOCKED_EVENT, onBlocked);
  }, []);

  useEffect(() => {
    if (!block) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setBlock(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [block]);

  if (!block) return null;
  const isTenant = block.code === 'NOT_YOUR_RESTAURANT';
  const showUsage = block.max != null && block.used != null;

  return (
    <div className="pg-overlay" onClick={() => setBlock(null)}>
      <div className="pg-dialog" role="dialog" aria-modal="true" aria-labelledby="pg-title" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pg-close" onClick={() => setBlock(null)} aria-label="Close"><X size={18} /></button>
        <div className={`pg-icon ${isTenant ? 'pg-icon--warn' : ''}`}>{isTenant ? <AlertTriangle size={26} /> : <Lock size={26} />}</div>
        <h3 id="pg-title">{TITLES[block.code] || 'Not available on your plan'}</h3>
        <p>{block.msg || block.message}</p>
        {showUsage && (
          <div className="pg-usage" aria-label={`${block.used} of ${block.max} used`}>
            <div className="pg-usage-bar"><span style={{ width: `${Math.min(100, (block.used / Math.max(1, block.max)) * 100)}%` }} /></div>
            <small>{block.used} of {block.max} used</small>
          </div>
        )}
        {block.plan?.name && <small className="pg-plan">Current plan: <b>{block.plan.name}</b></small>}
        <div className="pg-actions">
          <button type="button" className="pg-btn pg-btn--ghost" onClick={() => setBlock(null)}>Not now</button>
          {!isTenant && (
            <button type="button" className="pg-btn pg-btn--primary" onClick={() => { setBlock(null); openPlans(); }}>
              <Sparkles size={16} /> See plans
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* Trial / renewal / overdue / free-plan banner at the top of the dashboard */
export const PlanBanner = ({ cafeId }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(() => {
    fetchEntitlements(cafeId).then(setData).catch(() => {});
  }, [cafeId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    window.addEventListener(PLAN_CHANGED_EVENT, load);
    return () => { clearInterval(t); window.removeEventListener(PLAN_CHANGED_EVENT, load); };
  }, [load]);

  const banner = data?.banner;
  if (!banner || hidden) return null;
  const dismissible = banner.tone === 'info';
  return (
    <div className={`pg-banner pg-banner--${banner.tone}`} role="status">
      {banner.tone === 'info' ? <Info size={18} /> : <AlertTriangle size={18} />}
      <span>{banner.text}</span>
      <button type="button" className="pg-banner-cta" onClick={openPlans}>
        {banner.kind === 'renew' || banner.kind === 'grace' || banner.kind === 'past_due' ? 'Renew' : 'View plans'}
      </button>
      {dismissible && <button type="button" className="pg-banner-x" onClick={() => setHidden(true)} aria-label="Dismiss"><X size={16} /></button>}
    </div>
  );
};
